import React, { useState, useEffect, useContext } from 'react';
import './style.css';
import { PiggyBank, Receipt, CirclePercent, CirclePlus, CircleMinus, Award, GripHorizontal, Tag } from 'lucide-react';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import DIYIcon from '/src/assets/diy-icon-black-1.svg';
import { useDraggable } from '@dnd-kit/core';
import CleanProjectNames from '/src/services/CleanProjectNames';
import { WindowSizeContext } from '/src/App';

const ProjectsListCard = ({
    project,
    handleProjectClick,
    simulatorView = false,
    handleAddingProjectToSimulator,
    handleRemovingProjectFromSimulator,
    isSelected = false,
    showAllDetails = true,
    selectedClassToAdd = '',
    isBigPicture = false,
    isHighRanked = false,
    allowDragging = false
}) => {

    const { deviceType } = useContext(WindowSizeContext);

    // Use draggable only when not selected and not in simulator view
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: project.project_id,
        disabled: isSelected || simulatorView,
        data: {
            project: project
        },
        activationConstraint: {
            distance: 20, // Adjust as needed for your UX
        }
    });

    const imageUrl = `${import.meta.env.VITE_CONTENT_CDN_BASE_URL}/${project.project_image}`;
    // console.log('Image URL:', imageUrl);

    // Combine styles separately to ensure background image is always included
    const combinedStyles = {
        backgroundImage: `url("${imageUrl}")`,
        position: 'relative',
        ...(transform && !isSelected && !simulatorView ? {
            transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        } : {})
    };

    const project_name = CleanProjectNames(project.project_name);

    const project_discounts = project.project_discounts_company_logos;
    const project_num_discounts = project_discounts.length;
    const project_payoff = project.project_payoff_by_scenario[0].by_total.total

    // console.log('project in ProjectsListCard:', project);
    // console.log('simulatorView in ProjectsListCard:', simulatorView);
    // console.log('isSelected in ProjectsListCard:', isSelected);

    // Get the applicable climate risks from the project.project_associated_climate_risks array
    const applicable_risks = project?.project_associated_climate_risks || [];

    // Helper function to determine colors based on risk rating
    const getColors = (rating) => {
        switch (rating) {
            case 'Very High':
            case 'High':
                return {
                    background: 'var(--related_red)',
                    useWhiteIcon: true
                };
            case 'Relatively High':
                return {
                    background: 'var(--related_red)',
                    useWhiteIcon: true
                };
            case 'Medium':
            case 'Moderate':
            case 'Low':
                return {
                    background: 'var(--yellow)',
                    useWhiteIcon: false
                };
            default:
                return {
                    background: 'var(--very-light-grey)',
                    useWhiteIcon: false
                };
        }
    };

    // Get icon URL helper
    const getIconUrl = (risk) => {
        const colors = getColors(risk.risk_level);
        return CreateCloudfrontUrl(colors.useWhiteIcon ? risk.icon_white : risk.icon_black);
    };

    const howManyColumns = (!isBigPicture && project_num_discounts > 0) ? 'three-col' : 'two-col';

    return (
        <div
            className={`projects-list-card box-shadow-standard ${simulatorView ? 'simulator-view' : ''} ${selectedClassToAdd} ${isDragging ? 'is-dragging' : ''}`}
            key={`${project.project_id}-${project.project_name}`}
            style={combinedStyles}
        >
            {isHighRanked && (
                <div className={`projects-list-card-high-ranked-badge ${deviceType}`}>
                    <Award />
                </div>
            )}
            {isBigPicture && (
                <>
                    <div className={`projects-list-card-risks-container ${isBigPicture ? 'big-picture' : ''} ${deviceType}`}>
                        {project.project_associated_climate_risks.map((risk) => (
                            <div
                                key={risk.risk_name}
                                className="projects-list-card-risk-icon-wrapper"
                            >
                                <div
                                    className="projects-list-card-risk-icon-container box-shadow-standard"
                                    style={{ backgroundColor: getColors(risk.risk_level).background }}
                                >
                                    <img
                                        src={getIconUrl(risk)}
                                        alt={`${risk.risk_name} icon`}
                                        className="projects-list-card-risk-icon"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                    {project_num_discounts > 0 && (
                        <div className={`projects-list-card-detail-item big-picture discounts ${deviceType}`}>
                            <Tag className={`projects-list-card-detail-item-icon big-picture`} size={24} />
                            <span>{project_num_discounts}</span>
                        </div>
                    )}
                </>
            )}
            {simulatorView && (
                <button
                    className={`projects-list-card-simulator-toggle ${isSelected ? 'isSelected' : 'notSelected'}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (isSelected) {
                            handleRemovingProjectFromSimulator(project.project_id);
                        } else {
                            handleAddingProjectToSimulator(project.project_id);
                        }
                    }}
                    aria-label={isSelected ? "Remove from simulator" : "Add to simulator"}
                >
                    {isSelected ? (
                        <CircleMinus size={32} fill="var(--light-grey)" />
                    ) : (
                        <CirclePlus size={32} fill="var(--light-grey)" />
                    )}
                </button>
            )}
            {!(isSelected || simulatorView) && allowDragging && (
                <div
                    className="projects-list-card-drag-handle"
                    ref={setNodeRef}
                    {...attributes}
                    {...listeners}
                    tabIndex={-1}
                    aria-label="Drag project card"
                >
                    <GripHorizontal size={24} />
                </div>
            )}
            <div
                className="projects-list-card__overlay"
                role="button"
                tabIndex={0}
                onClick={() => handleProjectClick(project.project_id)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        handleProjectClick(project.project_id);
                    }
                }}
            >
                <div className="projects-list-card__info">
                    <span className={`projects-list-card__name ${isBigPicture ? 'big-picture' : ''} text-align-center`}>{project_name}</span>
                    {showAllDetails &&
                        <>
                            <div className={`projects-list-card-detail-items-container ${isBigPicture ? 'big-picture' : ''} ${howManyColumns}`}>

                                <div className="projects-list-card-detail-item payoff">
                                    <PiggyBank size={24} />
                                    <span>${project_payoff.toLocaleString()}</span>
                                </div>
                                {(!isBigPicture && project_num_discounts > 0) && (
                                    <div className="projects-list-card-detail-item discounts">
                                        <Tag size={24} />
                                        <span>{project_num_discounts}</span>
                                    </div>
                                )}
                                <div className="projects-list-card-detail-item cost">
                                    <Receipt size={24} />
                                    <span>${project.project_total_cost.toLocaleString()}</span>
                                </div>
                                {/* {isBigPicture && (
                                    <div className="projects-list-card-detail-item discounts">
                                        <Tag size={24} />
                                        <span>{project_num_discounts}</span>
                                    </div>
                                )} */}
                            </div>
                            {!isBigPicture && (
                                <div className={`projects-list-card-risks-container ${isBigPicture ? 'big-picture' : ''}`}>
                                    {project.project_associated_climate_risks.map((risk) => (
                                        <div
                                            key={risk.risk_name}
                                            className="projects-list-card-risk-icon-wrapper"
                                        >
                                            <div
                                                className="projects-list-card-risk-icon-container box-shadow-standard"
                                                style={{ backgroundColor: getColors(risk.risk_level).background }}
                                            >
                                                <img
                                                    src={getIconUrl(risk)}
                                                    alt={`${risk.risk_name} icon`}
                                                    className="projects-list-card-risk-icon"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    <div className="projects-list-card-risk-icon-wrapper">
                                        <div className={`projects-list-card-diy-container box-shadow-standard ${!project.project_diy_friendly ? 'not-diy-friendly' : ''}`}>
                                            <img
                                                src={DIYIcon}
                                                alt="DIY icon"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    }

                </div>
            </div>
        </div>
    );
};

export default ProjectsListCard;
